import { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  useDividends,
  useDividendsCalendar,
  useDividendsTotals,
  useDividendsProjection,
} from '../../hooks/useDividends';
import { PageLoader } from '../../components/common/LoadingSpinner';
import { hideCurrency, formatDate } from '../../utils/format';
import { useValuesVisibility } from '../../stores/valuesVisibilityStore';
import { exportDividendsToPDF, exportDividendsToExcel } from '../../utils/export';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import type { Dividend, DividendCalendarMonth } from '../../types';

type Tab = 'history' | 'calendar' | 'projection';

export default function DividendsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('history');
  const [year] = useState(new Date().getFullYear());

  const { data: dividends, isLoading } = useDividends(year);
  const { data: calendar } = useDividendsCalendar(year);
  const { data: totals } = useDividendsTotals();
  const { data: projection } = useDividendsProjection(12);
  const { isVisible } = useValuesVisibility();

  if (isLoading) return <PageLoader />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
        <div>
          <h1 className="page-title">Proventos</h1>
          <p className="page-subtitle">Dividendos, JCP e rendimentos</p>
        </div>
        {dividends && dividends.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportDividendsToExcel(dividends)}
              className="btn-ghost flex items-center gap-1.5 text-xs"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Excel
            </button>
            <button
              onClick={() => exportDividendsToPDF(dividends)}
              className="btn-ghost flex items-center gap-1.5 text-xs"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              PDF
            </button>
          </div>
        )}
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="stat-card">
          <span className="stat-label">Este Mês</span>
          <span className="stat-value text-success-500">
            {hideCurrency(totals?.monthly ?? 0, isVisible)}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Este Ano</span>
          <span className="stat-value text-success-500">
            {hideCurrency(totals?.yearly ?? 0, isVisible)}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Total Recebido</span>
          <span className="stat-value text-success-500">
            {hideCurrency(totals?.allTime ?? 0, isVisible)}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-surface-800 rounded-lg p-1 w-full sm:w-fit overflow-x-auto">
        {(['history', 'calendar', 'projection'] as Tab[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab
                ? 'bg-primary-600 text-white'
                : 'text-white/50 hover:text-white'
            }`}
          >
            {tab === 'history' ? 'Histórico' : tab === 'calendar' ? 'Calendário' : 'Projeção'}
          </button>
        ))}
      </div>

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="card">
          <h2 className="text-sm font-semibold text-white mb-4">
            Proventos {year} ({dividends?.count ?? 0} registros)
          </h2>

          {dividends?.dividends && dividends.dividends.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Ativo</th>
                    <th>Tipo</th>
                    <th className="text-right">Valor/Cota</th>
                    <th className="text-right">Total</th>
                    <th>Pagamento</th>
                    <th>Data EX</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {dividends.dividends.map((div: Dividend) => (
                    <tr key={div.id}>
                      <td className="font-mono font-medium text-white">
                        {div.assets?.code || '—'}
                      </td>
                      <td>
                        <span className="badge badge-blue">{div.type}</span>
                      </td>
                      <td className="text-right font-mono">
                        {hideCurrency(div.valuePerShare, isVisible)}
                      </td>
                      <td className="text-right font-mono text-success-500">
                        {hideCurrency(div.totalValue, isVisible)}
                      </td>
                      <td>{formatDate(div.paymentDate)}</td>
                      <td className="text-white/40">
                        {div.exDate ? formatDate(div.exDate) : '—'}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            div.isConfirmed ? 'badge-green' : 'badge-yellow'
                          }`}
                        >
                          {div.isConfirmed ? 'Confirmado' : 'Previsto'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-white/30 text-sm text-center py-12">
              Nenhum provento registrado em {year}
            </p>
          )}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="card">
          <h2 className="text-sm font-semibold text-white mb-6">
            Calendário de Proventos {year}
          </h2>

          {calendar ? (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={calendar} margin={{ bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
                    tickFormatter={(v) => v.substring(0, 3)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
                    tickFormatter={(v) =>
                      v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v
                    }
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.length) {
                        const d = payload[0].payload;
                        return (
                          <div className="bg-surface-800 border border-white/10 rounded-lg p-3 text-xs">
                            <p className="font-medium text-white">{d.month}</p>
                            <p className="text-success-500">{hideCurrency(d.total, isVisible)}</p>
                            <p className="text-white/40">{d.dividends.length} proventos</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="total" fill="#22c55e" radius={[4, 4, 0, 0]} opacity={0.8} />
                </BarChart>
              </ResponsiveContainer>

              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 mt-4">
                {calendar.map((month: DividendCalendarMonth) => (
                  <div
                    key={month.month}
                    className={`p-3 rounded-lg text-center ${
                      month.total > 0 ? 'bg-success-500/10 border border-success-500/20' : 'bg-white/[0.03]'
                    }`}
                  >
                    <p className="text-xs text-white/50 uppercase tracking-wider">
                      {month.month.substring(0, 3)}
                    </p>
                    <p
                      className={`text-sm font-medium mt-1 ${
                        month.total > 0 ? 'text-success-500' : 'text-white/20'
                      }`}
                    >
                      {month.total > 0 ? hideCurrency(month.total, isVisible) : '—'}
                    </p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-white/30 text-sm text-center py-12">
              Carregando calendário...
            </p>
          )}
        </div>
      )}

      {/* Projection Tab */}
      {activeTab === 'projection' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="stat-card">
              <span className="stat-label">Média Mensal Histórica</span>
              <span className="stat-value text-primary-400">
                {hideCurrency(projection?.historicalAvgMonthly ?? 0, isVisible)}
              </span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Projeção Anual</span>
              <span className="stat-value text-primary-400">
                {hideCurrency(projection?.historicalAvgAnnual ?? 0, isVisible)}
              </span>
            </div>
          </div>

          <div className="card">
            <h2 className="text-sm font-semibold text-white mb-4">
              Projeção dos Próximos 12 Meses
            </h2>

            {projection?.projection && (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={projection.projection}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis
                    dataKey="monthName"
                    tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.4)' }}
                    tickFormatter={(v) => v.split(' ')[0].substring(0, 3)}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.4)' }}
                    tickFormatter={(v) =>
                      v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v
                    }
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.length) {
                        const d = payload[0].payload;
                        return (
                          <div className="bg-surface-800 border border-white/10 rounded-lg p-3 text-xs">
                            <p className="font-medium text-white">{d.monthName}</p>
                            <p className="text-primary-400">
                              Projetado: {hideCurrency(d.projected, isVisible)}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="projected"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                    opacity={0.7}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
